import { useEffect, useMemo, useRef, useState } from "react";
import { t } from "ttag";

import { isEmail } from "metabase/lib/email";
import { ParameterWidget as StaticParameterWidget } from "metabase/parameters/components/ParameterWidget";
import type {
  EmbedResourceParameter,
  EmbeddingParameters,
} from "metabase/public/lib/types";
import {
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "metabase/ui";
import { getValuePopulatedParameters } from "metabase-lib/v1/parameters/utils/parameter-values";

import {
  type ExternalProxyRule,
  getProxyConfigAPI,
  saveProxyConfigAPI,
} from "./api";
import { useNestedModalEscape } from "./useNestedModalEscape";
import {
  getCombinedRuleEmails,
  getRuleFormValuesByParameterId,
  normalizeRuleForForm,
  sanitizeRuleParams,
  sanitizeRulesForLockedParameters,
} from "./utils";

interface ExternalAccessConfigModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccessShowLinks: () => void;
  lockedParameters: EmbedResourceParameter[];
  resource: {
    id: number | string;
    name: string;
    enable_embedding?: boolean;
    embedding_params?: EmbeddingParameters | null;
  };
  resourceType: "dashboard" | "question";
  user?: unknown;
  hasSettingsChanges: boolean;
}

const isValidEmails = (value: string) => {
  if (!value.trim()) {
    return true;
  }

  return value
    .split(",")
    .map((email) => email.trim())
    .filter(Boolean)
    .every((email) => isEmail(email));
};

const createRule = (): ExternalProxyRule => ({
  email: "",
  params: {},
});

export const ExternalAccessConfigModal = ({
  opened,
  onClose,
  onSuccessShowLinks,
  lockedParameters,
  resource,
  resourceType,
  user,
  hasSettingsChanges,
}: ExternalAccessConfigModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rules, setRules] = useState<ExternalProxyRule[]>([]);
  const [emails, setEmails] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const hasLockedParameters = lockedParameters.length > 0;

  const modalContentRef = useNestedModalEscape({ opened, onClose });
  const lockedParametersRef = useRef(lockedParameters);
  const lockedParametersKey = useMemo(
    () =>
      lockedParameters
        .map((parameter) =>
          JSON.stringify({
            default: parameter.default ?? null,
            id: parameter.id,
            name: parameter.name,
            required: parameter.required ?? false,
            slug: parameter.slug,
            type: parameter.type,
          }),
        )
        .join("|"),
    [lockedParameters],
  );

  const valuePopulatedRules = useMemo(
    () =>
      rules.map((rule) =>
        getValuePopulatedParameters({
          parameters: lockedParameters,
          values: getRuleFormValuesByParameterId(rule.params, lockedParameters),
          defaultRequired: false,
        }),
      ),
    [lockedParameters, rules],
  );

  useEffect(() => {
    lockedParametersRef.current = lockedParameters;
  }, [lockedParameters]);

  useEffect(() => {
    if (!opened || !resource.id) {
      return;
    }

    let isCancelled = false;
    const loadConfig = async () => {
      setIsLoading(true);
      try {
        const responseData = await getProxyConfigAPI({
          resource_id: resource.id,
          resource_type: resourceType,
        });
        if (isCancelled) {
          return;
        }

        if (hasLockedParameters) {
          setRules(
            responseData.rules && responseData.rules.length > 0
              ? responseData.rules.map((rule) =>
                  normalizeRuleForForm(rule, lockedParametersRef.current),
                )
              : [createRule()],
          );
        } else {
          setEmails(getCombinedRuleEmails(responseData.rules || []));
        }
        setValidationError(null);
      } catch {
        if (!isCancelled) {
          setRules([createRule()]);
          setEmails("");
          setValidationError(null);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadConfig();

    return () => {
      isCancelled = true;
    };
  }, [
    opened,
    resource.id,
    resourceType,
    hasLockedParameters,
    lockedParametersKey,
  ]);

  const updateRule = (
    index: number,
    updater: (rule: ExternalProxyRule) => ExternalProxyRule,
  ) => {
    setValidationError(null);
    setRules((currentRules) =>
      currentRules.map((rule, ruleIndex) =>
        ruleIndex === index ? updater(rule) : rule,
      ),
    );
  };

  const handleSave = async () => {
    setValidationError(null);
    const finalRules = hasLockedParameters
      ? sanitizeRulesForLockedParameters(rules, lockedParameters)
      : emails.trim()
        ? [{ email: emails.trim() }]
        : [];

    const hasInvalidEmails = finalRules.some(
      (rule) => !isValidEmails(rule.email || ""),
    );
    if (hasInvalidEmails) {
      setValidationError(
        t`Please ensure all emails are valid and separated by commas.`,
      );
      return;
    }

    if (hasLockedParameters) {
      const duplicateRuleKey = finalRules.reduce<Record<string, number>>(
        (acc, rule) => {
          const ruleKey = JSON.stringify(
            sanitizeRuleParams(rule.params, lockedParameters),
          );
          acc[ruleKey] = (acc[ruleKey] || 0) + 1;
          return acc;
        },
        {},
      );
      const hasDuplicateRule = Object.values(duplicateRuleKey).some(
        (count) => count > 1,
      );

      if (hasDuplicateRule) {
        setValidationError(
          t`Each locked parameter combination must be unique before saving.`,
        );
        return;
      }
    }

    setIsSaving(true);
    try {
      await saveProxyConfigAPI({
        resource_id: resource.id,
        resource_type: resourceType,
        report_name: resource.name,
        rules: finalRules,
        is_published: resource.enable_embedding,
        user,
      });

      onClose();
      if (resource.enable_embedding && !hasSettingsChanges) {
        onSuccessShowLinks();
      }
    } catch (error) {
      setValidationError(
        error instanceof Error
          ? error.message
          : t`Failed to save external access configuration.`,
      );
      return;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      closeOnEscape={false}
      title={t`Configure External User Access`}
      size="xl"
    >
      <div ref={modalContentRef}>
        <Stack gap="lg">
          <Text>
            {hasLockedParameters
              ? t`Define rules by mapping locked parameter values to external emails.`
              : t`Enter the email addresses for external users who should have access.`}
          </Text>
          <Text>{t`Use a comma (,) to separate multiple email addresses.`}</Text>

          {validationError ? (
            <Text style={{ color: "var(--mb-color-danger)" }}>
              {validationError}
            </Text>
          ) : null}

          {isLoading ? (
            <Text>{t`Loading configuration...`}</Text>
          ) : hasLockedParameters ? (
            <Stack gap="md">
              {rules.map((rule, index) => (
                <Stack
                  key={`${index}-${rule.id ?? "new"}`}
                  gap="sm"
                  p="md"
                  style={{ border: "1px solid var(--mb-color-border)" }}
                >
                  {valuePopulatedRules[index]?.map((parameter) => (
                    <StaticParameterWidget
                      key={parameter.id}
                      parameter={parameter}
                      parameters={valuePopulatedRules[index]}
                      setValue={(value) => {
                        updateRule(index, (currentRule) => ({
                          ...currentRule,
                          params: {
                            ...currentRule.params,
                            [parameter.slug]: value,
                          },
                        }));
                      }}
                      setParameterValueToDefault={() => {
                        updateRule(index, (currentRule) => ({
                          ...currentRule,
                          params: {
                            ...currentRule.params,
                            [parameter.slug]: parameter.default,
                          },
                        }));
                      }}
                      enableParameterRequiredBehavior
                    />
                  ))}

                  <TextInput
                    label={t`External Email(s)`}
                    error={
                      isValidEmails(rule.email || "")
                        ? undefined
                        : t`Please ensure all emails are valid and separated by commas.`
                    }
                    value={rule.email || ""}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      updateRule(index, (currentRule) => ({
                        ...currentRule,
                        email: value,
                      }));
                    }}
                  />

                  <Group justify="flex-end">
                    <Button
                      aria-label={t`Remove`}
                      variant="subtle"
                      disabled={rules.length === 1}
                      onClick={() => {
                        setValidationError(null);
                        setRules((currentRules) =>
                          currentRules.filter(
                            (_, ruleIndex) => ruleIndex !== index,
                          ),
                        );
                      }}
                    >
                      {t`Remove`}
                    </Button>
                  </Group>
                </Stack>
              ))}

              <Group justify="flex-start">
                <Button
                  variant="default"
                  onClick={() => {
                    setValidationError(null);
                    setRules((currentRules) => [...currentRules, createRule()]);
                  }}
                >
                  {t`Add Rule`}
                </Button>
              </Group>
            </Stack>
          ) : (
            <Textarea
              label={t`External Email(s)`}
              minRows={3}
              error={
                isValidEmails(emails)
                  ? undefined
                  : t`Please ensure all emails are valid and separated by commas.`
              }
              value={emails}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setValidationError(null);
                setEmails(value);
              }}
            />
          )}

          <Group justify="flex-end">
            <Button onClick={onClose}>{t`Cancel`}</Button>
            <Button variant="filled" loading={isSaving} onClick={handleSave}>
              {t`Save`}
            </Button>
          </Group>
        </Stack>
      </div>
    </Modal>
  );
};
